import { Buffer } from 'buffer';

const ENCRYPTION_VERSION = 1;
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

export async function generateEncryptionKey(userId: string, sessionId: string): Promise<CryptoKey> {
  // Create a key based on user session
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(`${userId}-${sessionId}`),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new TextEncoder().encode('jaqln-conversations'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptData(data: any, key: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encoded = new TextEncoder().encode(JSON.stringify(data));

  const encrypted = await crypto.subtle.encrypt(
    {
      name: ALGORITHM,
      iv
    },
    key,
    encoded
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  return Buffer.from(combined).toString('base64');
}

export async function decryptData(encrypted: string, key: CryptoKey): Promise<any> {
  const data = Buffer.from(encrypted, 'base64');
  const iv = data.slice(0, IV_LENGTH);
  const content = data.slice(IV_LENGTH);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: ALGORITHM,
      iv
    },
    key,
    content
  );

  return JSON.parse(new TextDecoder().decode(decrypted));
} 